import React, { useEffect, useState } from "react";
import { useFormik, Formik, Field, Form, ErrorMessage } from "formik";
import { toast } from "react-toastify";
import * as Yup from "yup";
import MenuApi from "../../../../App/api/menu.apis";
import { useFetchAll } from "../../../../custom/FetchApiHook";
import selectDropdown from "../../../../custom/SelectDropdown";
import { RegionMasterApi } from "../../../../App/api/RegionMaster";
import SelectDropdown from "../../../../Components/Dropdown/SelectDropdown";
import SidebarField from "../../../../Components/SidebarField";
import { FormField } from "../../../../Components/FormField";
import Button from "../../../../Components/Button";
import TextInput from "../../../../Components/TextInput";

// Validation schema
const validationSchema = Yup.object({
  regionType: Yup.object().nullable().required("Region type is required"),
  city: Yup.string().required("City is required"),
  zoneCode: Yup.string().required("Zone Code is required"),
  additionalFee: Yup.number().required("Additional Fee is required"),
  referralFeeRate: Yup.number().required("Referral Fee Rate is required"),
  closingFeeRate: Yup.number().required("Closing Fee Rate is required"),
  shippingFeeRate: Yup.number().required("Shipping Fee Rate is required"),
  shippingHandlingFeeFixedRate: Yup.number().required(
    "Shipping Handling Fee Fixed Rate is required"
  ),
});

function AddRegionModel({ isOpen, handleClose, data, fetchAllData }) {
  const initialValues = {
    regionType: data?.regionType || null,
    country: data?.country?._id || "",
    city: data?.city?._id || "",
    zoneCode: data?.zoneCode || "",
    additionalFee: data?.additionalFee || 0.0,
    referralFeeRate: data?.referralFeeRate || 0.0,
    closingFeeRate: data?.closingFeeRate || 0.0,
    shippingFeeRate: data?.shippingFeeRate || 0.0,
    shippingHandlingFeeFixedRate: data?.shippingHandlingFeeFixedRate || 0.0,
  };
  // console.log("data from Region Type:", data);

  const handleSubmit = async (values) => {
    console.log("Payload to send:", values);

    try {
      const createRes = data?._id
        ? await RegionMasterApi.updateRegion(values, data?._id)
        : await RegionMasterApi.createRegion(values);

      // console.log("Create response:", createRes.data?.data);
      toast.success(
        data?._id ? "Region Details Updated" : "Region Details Created"
      );
      fetchAllData();
    } catch (err) {
      console.error("Error updating/creating region:", err);
      toast.error(
        err?.response?.data?.message || err?.message || "An error occurred"
      );
    } finally {
      handleClose();
    }
  };

  // Fetch country data
  const countryData = useFetchAll(() => MenuApi.getAllCountry());
  const [cityData, setCityData] = useState([]);
  const cityOptions = selectDropdown(cityData, "id", "name");
  const [selectedOption, setSelectedOption] = useState(null);

  // Fetch cities when modal opens with existing data
  useEffect(() => {
    if (isOpen && data?.country?._id) {
      handleCountryChange(data.country._id);
    }
  }, [isOpen, data?.country?._id]);

  const handleCountryChange = async (selected) => {
    setSelectedOption(selected);
    // console.log("Selected value:", selected);
    try {
      const res = await MenuApi.getAllCity(selected);
      // console.log("res", res?.data?.data);
      setCityData(res?.data?.data);
    } catch (error) {
      // console.log("error", error);
    }
  };


  if (isOpen) {
    return (
      <div>
        <Formik
          initialValues={initialValues}
          onSubmit={handleSubmit}
          enableReinitialize={true}
        //   validationSchema={RolePermissionsSchema}
        >
          {({ handleChange, setFieldValue, values, handleSubmit }) => (
            <Form className="w-full gap-4 flex flex-col bg-transparent py-5 px-3">
              <SidebarField
                handleClose={handleClose}
                title={
                  data?._id ? "Update Region Details" : "Add Region Details"
                }
                button1={
                  <Button text="Cancel" type="button" onClick={handleClose} />
                }
                button2={
                  <Button
                    text={data?._id ? "Update" : "Submit"}
                    type="submit"
                  />
                }
              >
                <FormField label="Region Type" tooltip="Select Region type">
                  <SelectDropdown
                    sx={{ borderRadius: "3px", height: "2.6rem", border: "1px solid #d0d0d0" }}
                    value={values.regionType}
                    inputName="regionType"
                    placeholder="Select Region"
                    options={[
                      { name: "Urban", value: "Urban" },
                      { name: "Rural", value: "Rural" },
                      { name: "Remote Area", value: "Remote Area" },
                    ]}
                    handleFilter={(name, v) => setFieldValue("regionType", v)}
                  />
                  <ErrorMessage
                    name="regionType"
                    component="p"
                    className="error"
                  />
                </FormField>

                <FormField label="Country" tooltip="Select Country">
                  <SelectDropdown
                    sx={{ borderRadius: "3px", height: "2.6rem", border: "1px solid #d0d0d0" }}
                    value={values.country}
                    inputName="country"
                    placeholder="Select Country"
                    options={countryData.value.data || []}
                    optionLabel="name"
                    optionValue="_id"
                    handleFilter={(name, v) => {
                      setFieldValue('country', v)
                      handleCountryChange(v)
                    }}
                  />
                  <ErrorMessage
                    name="country"
                    component="p"
                    className="error"
                  />
                </FormField>

                <FormField label="City" tooltip="Select City">
                  <SelectDropdown
                    sx={{ borderRadius: "3px", height: "2.6rem", border: "1px solid #d0d0d0" }}
                    value={values.city}
                    inputName="city"
                    placeholder="Select City"
                    options={cityData || []}
                    optionLabel="name"
                    optionValue="_id"
                    handleFilter={(name, v) => {
                      setFieldValue('city', v)
                    }}
                  />
                  <ErrorMessage name="city" component="p" className="error" />
                </FormField>

                <FormField label="Zone Code" tooltip="Enter Zone Code">
                  <TextInput
                    name={"zoneCode"}
                    type={"text"}
                    placeholder={"Enter Zone Code"}
                    onChange={handleChange}
                    value={values?.zoneCode}
                  />
                  <ErrorMessage
                    name="zoneCode"
                    component="p"
                    className="error"
                  />
                </FormField>

                <div className="flex justify-between gap-4">
                  <FormField
                    label="Referral Fee Rate (GHS)"
                    tooltip="Enter Referral Fee Rate"
                  >
                    <TextInput
                      name={"referralFeeRate"}
                      type={"number"}
                      placeholder={"Enter Referral Fee Rate"}
                      onChange={handleChange}
                      value={values?.referralFeeRate}
                    />
                    <ErrorMessage
                      name="referralFeeRate"
                      component="p"
                      className="error"
                    />
                  </FormField>

                  <FormField
                    label="Closing Fee Rate (GHS)"
                    tooltip="Enter Closing Fee Rate"
                  >
                    <TextInput
                      name={"closingFeeRate"}
                      type={"number"}
                      placeholder={"Enter Closing Fee Rate"}
                      onChange={handleChange}
                      value={values?.closingFeeRate}
                    />
                    <ErrorMessage
                      name="closingFeeRate"
                      component="p"
                      className="error"
                    />
                  </FormField>
                </div>

                <FormField
                  label="Shipping Fee Rate (GHS)"
                  tooltip="Enter Shipping Fee Rate"
                >
                  <TextInput
                    name={"shippingFeeRate"}
                    type={"number"}
                    placeholder={"Enter Shipping Fee Rate"}
                    onChange={handleChange}
                    value={values?.shippingFeeRate}
                  />
                  <ErrorMessage
                    name="shippingFeeRate"
                    component="p"
                    className="error"
                  />
                </FormField>

                <FormField
                  label="Shipping Handling Fee Fixed Rate (GHS)"
                  tooltip="Enter Shipping Handling Fee Fixed Rate"
                >
                  <TextInput
                    name={"shippingHandlingFeeFixedRate"}
                    type={"number"}
                    placeholder={"Enter Shipping Handling Fee Fixed Rate"}
                    onChange={handleChange}
                    value={values?.shippingHandlingFeeFixedRate}
                  />
                  <ErrorMessage
                    name="shippingHandlingFeeFixedRate"
                    component="p"
                    className="error"
                  />
                </FormField>

                <FormField
                  label="Additional Fee (GHS)"
                  tooltip="Enter Additional Fee"
                >
                  <TextInput
                    name={"additionalFee"}
                    type={"number"}
                    placeholder={"Enter Additional Fee"}
                    onChange={handleChange}
                    value={values?.additionalFee}
                  />
                  <ErrorMessage
                    name="additionalFee"
                    component="p"
                    className="error"
                  />
                </FormField>
              </SidebarField>
            </Form>
          )}
        </Formik>
      </div>
    );
  }
  return null;


}

export default AddRegionModel;
